import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Search, Zap, Coins, Menu, X, User, LogOut, Play, Shield, DollarSign, Music } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import NotificationsBell from "@/components/NotificationsBell";
import { InstallButton } from "@/components/PwaInstall";

const EMBEDDED = (() => { try { return window.self !== window.top; } catch { return true; } })();

export default function Navbar() {
  if (EMBEDDED) return null; // the parent Jixxeo window already shows the header
  return <NavbarInner />;
}

function NavbarInner() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/browse?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid={NAV.root}
      className="sticky top-0 z-50 glass-strong border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
          <span className="relative w-8 h-8 rounded-md bg-[#00F0FF] flex items-center justify-center glow-cyan">
            <Zap className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <span className="font-heading font-black text-xl tracking-tighter uppercase">
            Jixxeo<span className="text-[#00F0FF]">.</span>Apps
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          <NavLink to="/feed" data-testid={NAV.feedLink} className={({ isActive }) => `px-3 py-2 mono text-xs uppercase tracking-widest transition-colors flex items-center gap-1 ${isActive ? "text-[#00F0FF]" : "text-white/60 hover:text-white"}`}>
            <Play className="w-3 h-3" /> Feed
          </NavLink>
          <NavLink to="/loops" data-testid="nav-loops-link" className={({ isActive }) => `px-3 py-2 mono text-xs uppercase tracking-widest transition-colors flex items-center gap-1 ${isActive ? "text-[#39FF14]" : "text-white/60 hover:text-white"}`}>
            <Music className="w-3 h-3" /> Loops
          </NavLink>
          <a href="/apps/jixxeo-power" data-testid="nav-power-link"
            className="px-3 py-2 mono text-xs uppercase tracking-widest transition-colors flex items-center gap-1 text-[#1E88FF] hover:text-white">
            <Zap className="w-3 h-3" fill="currentColor" /> Power
          </a>
          {[
            { to: "/browse", label: "Browse", tid: NAV.browseLink },
            { to: "/playzone", label: "PlayZone", tid: "nav-playzone-link" },
            { to: "/categories", label: "Categories", tid: NAV.categoriesLink },
            { to: "/developers", label: "Developers", tid: NAV.developersLink },
            { to: "/roadmap", label: "Roadmap", tid: "nav-roadmap-link" },
          ].map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.tid}
              className={({ isActive }) => `px-3 py-2 mono text-xs uppercase tracking-widest transition-colors ${isActive ? "text-[#00F0FF]" : "text-white/60 hover:text-white"}`}>
              {l.label}
            </NavLink>
          ))}
          {user?.is_developer && (
            <NavLink to="/dev/apps"
              className={({ isActive }) => `px-3 py-2 mono text-xs uppercase tracking-widest transition-colors ${isActive ? "text-[#39FF14]" : "text-[#39FF14]/70 hover:text-[#39FF14]"}`}>
              Publish
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin"
              className={({ isActive }) => `px-3 py-2 mono text-xs uppercase tracking-widest transition-colors flex items-center gap-1 ${isActive ? "text-[#FF003C]" : "text-[#FF003C]/70 hover:text-[#FF003C]"}`}>
              <Shield className="w-3 h-3" /> Admin
            </NavLink>
          )}
        </nav>

        <form onSubmit={submit} className="flex-1 max-w-md ml-auto hidden md:block">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              data-testid={NAV.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search apps, games, devs…"
              className="w-full pl-11 pr-20 py-2.5 bg-white/[0.04] border border-white/10 rounded-full text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00F0FF]/60 focus:bg-white/[0.06] transition-colors"
            />
            <button type="submit" data-testid={NAV.searchBtn} className="absolute right-1.5 top-1/2 -translate-y-1/2 mono text-[10px] uppercase tracking-widest bg-white/10 hover:bg-[#00F0FF] hover:text-black text-white/80 px-3 py-1.5 rounded-full transition-colors">
              Go
            </button>
          </div>
        </form>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-2 ml-2">
          <InstallButton />
          {user ? (
            <>
              <NotificationsBell />
              <Link to="/earn" className="hidden md:inline-flex items-center gap-1.5 mono text-xs px-3 py-2 rounded-full border border-[#39FF14]/30 bg-[#39FF14]/10 text-[#39FF14] hover:border-[#39FF14] transition-colors">
                <DollarSign className="w-3.5 h-3.5" /> Wallet
              </Link>
              <Link to="/coins" data-testid={NAV.coinsChip} className="inline-flex items-center gap-1.5 mono text-xs px-3 py-2 rounded-full border border-[#FFE300]/30 bg-[#FFE300]/10 text-[#FFE300] hover:border-[#FFE300] transition-colors">
                <Coins className="w-3.5 h-3.5" />
                <span>{user.coins ?? 0}</span>
              </Link>
              <div className="relative">
                <button onClick={() => setMenuOpen(!menuOpen)} data-testid={NAV.profileMenu} className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#00F0FF]/50" style={{ background: `${user.avatar_color || "#00F0FF"}22`, color: user.avatar_color || "#00F0FF" }}>
                  <span className="font-heading font-black text-sm">{(user.name || user.email || "?")[0].toUpperCase()}</span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-11 w-52 glass-strong rounded-xl overflow-hidden border border-white/10">
                    <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 mono text-xs uppercase tracking-widest text-white/80 hover:bg-white/5">
                      <User className="w-3.5 h-3.5" /> Profile
                    </Link>
                    <Link to="/coins" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 mono text-xs uppercase tracking-widest text-white/80 hover:bg-white/5">
                      <Coins className="w-3.5 h-3.5" /> Coin Store
                    </Link>
                    <button onClick={doLogout} data-testid={NAV.logoutBtn} className="w-full text-left flex items-center gap-2 px-4 py-3 mono text-xs uppercase tracking-widest text-[#FF003C] hover:bg-white/5 border-t border-white/5">
                      <LogOut className="w-3.5 h-3.5" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" data-testid={NAV.loginBtn} className="mono text-xs uppercase tracking-widest text-white/70 hover:text-white px-3 py-2">
                Login
              </Link>
              <Link to="/register" data-testid={NAV.registerBtn} className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors">
                Join
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden ml-auto text-white/80" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 px-6 py-4 space-y-3 glass-strong">
          <form onSubmit={submit}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full pl-11 pr-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-full text-sm" />
            </div>
          </form>
          {["/feed", "/loops", "/browse", "/categories", "/developers", "/roadmap"].map((to) => (
            <Link key={to} to={to} onClick={() => setOpen(false)}
              data-testid={to === "/roadmap" ? "nav-roadmap-link-mobile" : undefined}
              className="block mono text-xs uppercase tracking-widest text-white/70 py-2">
              {to.replace("/", "")}
            </Link>
          ))}
          {!user && (
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 mono text-xs uppercase text-center py-2 border border-white/15 rounded-full">Login</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 mono text-xs uppercase text-center py-2 bg-[#00F0FF] text-black rounded-full font-bold">Join</Link>
            </div>
          )}
          {user && (
            <div className="pt-2 border-t border-white/10 space-y-2">
              <Link to="/coins" onClick={() => setOpen(false)} className="block mono text-xs uppercase text-[#FFE300]">Coins · {user.coins}</Link>
              <Link to="/profile" onClick={() => setOpen(false)} className="block mono text-xs uppercase text-white/70">Profile</Link>
              <button onClick={doLogout} className="block mono text-xs uppercase text-[#FF003C]">Sign Out</button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
